// src/components/activities/ActivityFeed.jsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  Divider,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Alert,
  useTheme,
  alpha
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Schedule as TimeIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Comment as CommentIcon,
  FlagCircle as MilestoneIcon,
  CheckCircle as CompletedIcon,
  TaskAlt as TaskIcon,
  Group as TeamIcon,
  ArrowForward as StatusChangeIcon,
  Person as AssignIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import SecureAvatar from '../ui/SecureAvatar';

// Helper function to format dates
const formatDate = (date) => {
  if (!date) return '';
  try {
    return format(new Date(date), 'MMM d, yyyy h:mm a');
  } catch (error) {
    return '';
  }
};

// Helper function to get activity icon
const getActivityIcon = (activity) => {
  const { activityType, entityType } = activity;
  
  switch (activityType) {
    case 'create':
      return <AddIcon />;
    case 'update':
      return <EditIcon />;
    case 'delete':
      return <DeleteIcon />;
    case 'status_change':
      return <StatusChangeIcon />;
    case 'comment':
      return <CommentIcon />;
    case 'add_milestone':
      return <MilestoneIcon />;
    case 'complete_milestone':
      return <CompletedIcon />;
    case 'add_task':
      return <TaskIcon />;
    case 'add_team_member':
    case 'remove_team_member':
      return <TeamIcon />;
    case 'assign':
      return <AssignIcon />;
    default:
      // Default icon based on entity type
      if (entityType === 'task') return <TaskIcon />;
      if (entityType === 'milestone') return <MilestoneIcon />;
      if (entityType === 'comment') return <CommentIcon />;
      return <TimeIcon />;
  }
};

// Helper function to get activity color
const getActivityColor = (activity, theme) => {
  const { activityType } = activity;
  
  switch (activityType) {
    case 'create':
      return theme.palette.success.main;
    case 'update':
      return theme.palette.info.main;
    case 'delete':
      return theme.palette.error.main;
    case 'status_change':
      return theme.palette.warning.main;
    case 'comment':
      return theme.palette.secondary.main;
    case 'add_milestone':
    case 'complete_milestone':
      return theme.palette.primary.main;
    case 'add_team_member':
    case 'remove_team_member':
      return theme.palette.info.main;
    default:
      return theme.palette.text.secondary;
  }
};

/**
 * ActivityFeed component for displaying activity feed/history
 */
const ActivityFeed = ({ 
  activities = [],
  isLoading = false,
  error = null,
  hasMore = false,
  onLoadMore,
  onSearch,
  onFilter,
  filters = [],
  title = 'Activity Feed'
}) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  
  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(searchTerm);
    }
  };
  
  // Handle clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    if (onSearch) {
      onSearch('');
    }
  };
  
  // Handle filter change
  const handleFilterChange = (filterValue) => {
    setActiveFilter(filterValue);
    if (onFilter) {
      onFilter(filterValue);
    }
  };
  
  return (
    <Box>
      {/* Header with title */}
      {title && (
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
      )}
      
      {/* Search and filters */}
      <Box sx={{ mb: 3 }}>
        <form onSubmit={handleSearch}>
          <TextField
            fullWidth
            placeholder="Search activities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    aria-label="clear search"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
            size="small"
            sx={{ mb: 2 }}
          />
        </form>
        
        {/* Filters */}
        {filters.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {filters.map((filter) => (
              <Chip
                key={filter.value}
                label={filter.label}
                clickable
                color={activeFilter === filter.value ? 'primary' : 'default'}
                variant={activeFilter === filter.value ? 'filled' : 'outlined'}
                onClick={() => handleFilterChange(filter.value)}
                icon={filter.icon || <FilterIcon />}
              />
            ))}
          </Box>
        )}
      </Box>
      
      {/* Activities list */}
      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        {isLoading && activities.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : activities.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              No activities found.
            </Typography>
          </Box>
        ) : (
          <List sx={{ width: '100%', bgcolor: 'background.paper', p: 0 }}>
            {activities.map((activity, index) => (
              <React.Fragment key={activity._id || index}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: alpha(getActivityColor(activity, theme), 0.1),
                        color: getActivityColor(activity, theme)
                      }}
                    >
                      {getActivityIcon(activity)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2">
                        {activity.title || 'Activity'}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ display: 'block', mb: 1 }}
                        >
                          {activity.description || ''}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                          {activity.user && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                              <SecureAvatar
                                src={activity.user.photo}
                                alt={activity.user.firstName}
                                sx={{ width: 24, height: 24, mr: 0.5 }}
                              >
                                {activity.user.firstName[0]}
                              </SecureAvatar>
                              <Typography variant="caption" color="textSecondary">
                                {activity.user.firstName} {activity.user.lastName}
                              </Typography>
                            </Box>
                          )}
                          
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}>
                            <TimeIcon fontSize="small" sx={{ mr: 0.5, fontSize: 14 }} />
                            {formatDate(activity.createdAt)}
                          </Typography>
                          
                          {activity.activityType && (
                            <Chip
                              label={activity.activityType.replace(/_/g, ' ')}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          
                          {activity.entityType && (
                            <Chip
                              label={activity.entityType}
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          )}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
        
        {/* Load more button */}
        {hasMore && activities.length > 0 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={onLoadMore}
              disabled={isLoading}
              startIcon={isLoading && <CircularProgress size={20} />}
            >
              {isLoading ? 'Loading...' : 'Load More'}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ActivityFeed;